import Card from "@/atoms/Card";
import LineBreak from "@/atoms/LineBreak";
import CheckoutOrderItem from "components/checkout/CheckoutOrderItem";
import CheckoutOrderFees from "components/checkout/CheckoutOrderFees";
import CheckoutRewardVoucher from "components/checkout/CheckoutRewardVoucher";
import styled from "styled-components";
import {
  OrderSummary as OrderSummaryType,
  OrderSummaryItem,
} from "lib/api/checkout";
import {
  PostalAddressSchema,
  SnakeToCamelCaseNested,
} from "@literati/kids-api";
import { Subscription } from "lib/api/subscription";

export interface Invoice {
  summaryTable: OrderSummaryType["orderSummary"];
  items: any[];
  shippingAddress?: SnakeToCamelCaseNested<PostalAddressSchema>;
}
const getNormalizedOrderSummary = (invoice: Invoice = {} as Invoice) => {
  const { summaryTable, items } = invoice;
  const item = items?.[0]?.object;
  const childName = item?.extra.child.firstName;
  const planName = item?.planName;
  const summary: { [key: string]: OrderSummaryItem } = {};
  summaryTable?.forEach((item) => {
    summary[item?.type] = item;
  });
  return {
    summary,
    childName,
    planName,
  };
};

const StyledOrderSummary = styled.section`
  .line-item {
    display: flex;
    justify-content: space-between;
    .img-container {
      background-size: auto 100%;
      width: 60px;
      height: 60px;
      flex-shrink: 0;
      border-radius: 9px;
    }
  }
`;

interface OrderSummaryProps {
  invoice: Invoice;
  session: any;
  subscription: Subscription;
  className?: string;
  readingLevelBoxImages: any;
}

const OrderSummary = ({
  invoice,
  session,
  subscription,
  readingLevelBoxImages,
  className = "",
}: OrderSummaryProps) => {
  const { summary, childName, planName } = getNormalizedOrderSummary(invoice);
  const email = session?.accountInfo?.email;
  const shippingAddress =
    invoice?.shippingAddress || subscription?.shippingAddress;
  const redeemingGift = summary?.item?.item?.type === "prepaid_box";
  const paymentMethod = session?.defaultPaymentMethod?.description;
  const readingLevelName = subscription?.readingLevelName || "";
  const readingLevelBoxImage =
    readingLevelBoxImages?.[readingLevelName?.toLowerCase?.()]?.fields;

  const orderItemDescriptionPlanName = planName?.replace("Monthly", "");

  return (
    <StyledOrderSummary
      id="order-summary"
      data-analytics="sub-checkout-confirmation"
      className={className}
    >
      <Card className="px-lg py-sm">
        <div className={"py-sm"}>
          <div className="font-medium text-black mb-2">Order</div>
          <CheckoutOrderItem
            item={{
              name: readingLevelName,
              description: `${
                redeemingGift ? "Gift Membership" : orderItemDescriptionPlanName
              } for ${childName}`,
              image: readingLevelBoxImage,
            }}
          />
          <CheckoutOrderFees
            orderSummaryDetails={summary}
            redeemingGift={redeemingGift}
            totalOnly={false}
          />
        </div>
        <CheckoutRewardVoucher orderSummaryDetails={summary} />
        <div className="py-md text-black">
          <div className="font-medium mb-2">Account</div>
          <div className={"text-sm"}>
            <p>{email}</p>
          </div>
        </div>
        <LineBreak />
        {shippingAddress ? (
          <div className="py-md text-black">
            <div className="font-medium mb-2">Shipping Details</div>
            <div className={"text-sm"}>
              <p>{shippingAddress?.name}</p>
              <p>{shippingAddress?.street1}</p>
              {shippingAddress?.street2 && <p>{shippingAddress?.street2}</p>}
              <p>
                {shippingAddress?.city}, {shippingAddress?.state}{" "}
                {shippingAddress?.postalCode}
              </p>
            </div>
          </div>
        ) : null}
        {paymentMethod && (
          <>
            <LineBreak />
            <div className="py-md text-black">
              <div className="font-medium mb-2">Payment Method</div>
              <div className={"text-sm"}>
                <p>{paymentMethod}</p>
              </div>
            </div>
          </>
        )}
      </Card>
    </StyledOrderSummary>
  );
};

export default OrderSummary;
